import { ContentElementSchema } from "@schema-types/types/JourneySchema";
import { prefixCssWithId } from "../../_common/prefixCssWithId";
import { useState, useEffect } from "react";
import { getContentElementContent } from "@/data/journeyContent/api";
import { useUserJourneyContextOptional } from "@/context/UserJourneyContext";
import { Loader2 } from "lucide-react";

export type ContentElementProps = {
  element: ContentElementSchema;
  formStepId: string;
  initialHtml?: string;
  initialCss?: string;
};

export const ContentElement = ({ element, formStepId, initialHtml, initialCss }: ContentElementProps) => {
  const journeyContext = useUserJourneyContextOptional();
  const [html, setHtml] = useState(initialHtml ?? "");
  const [css, setCss] = useState(initialCss ?? "");
  const [loading, setLoading] = useState(initialHtml === undefined && initialCss === undefined);

  useEffect(() => {
    if (initialHtml !== undefined || initialCss !== undefined) {
      setHtml(initialHtml ?? "");
      setCss(initialCss ?? "");
      setLoading(false);
      return;
    }
    if (!journeyContext?.journey?.id || !formStepId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    const version = journeyContext.journeyVersion ?? "dev";
    getContentElementContent(journeyContext.journey.id, formStepId, element.id, version).then((content) => {
      if (!cancelled && content) {
        setHtml(content.html ?? "");
        setCss(content.css ?? "");
      }
      if (!cancelled) setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [journeyContext?.journey?.id, formStepId, element.id, journeyContext?.journeyVersion, initialHtml, initialCss]);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[80px]">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }

  const scopedCss = css ? prefixCssWithId(css, element.id) : undefined;
  return (
    <div id={element.id} className="jb-content-element space-y-3">
      {scopedCss && <style id={`${element.id}-css`}>{scopedCss}</style>}
      <div dangerouslySetInnerHTML={{ __html: html }} />
    </div>
  );
};
